import { z } from "zod";
import type { JiaxingCase } from "../types/journey";
import { TopicSchema } from "./schemas";

export const CaseMatchRequestSchema = z.object({
  sessionId: z.string().min(8).max(120),
  originalQuestion: z.string().min(5).max(120),
  firstAnswer: z.string().min(1).max(120),
  secondAnswer: z.string().min(1).max(120),
  thirdAnswer: z.string().min(1).max(80),
  topic: TopicSchema.optional(),
});
export type CaseMatchRequest = z.infer<typeof CaseMatchRequestSchema>;
export const CaseSelectionSchema = z.object({
  case_id: z.string().max(80).nullable(),
  user_excerpt: z.string().max(45),
  case_excerpt: z.string().max(80),
  generated_answer: z.string().max(260),
});
export type CaseSelection = z.infer<typeof CaseSelectionSchema>;
export type CaseMatchInput = CaseMatchRequest & {
  candidates: Array<Pick<JiaxingCase, "id" | "title" | "summary" | "content"> & { tags?: string[]; priority?: number | null }>;
};
export interface CaseMatchResult {
  item: JiaxingCase | null;
  reason: string;
  generatedAnswer: string;
  method: "ai" | "keyword" | "theme" | "generated";
}
export function eligibleCases(items: JiaxingCase[]): JiaxingCase[] {
  return items.filter(item => item.review_status === "published" && item.environment === "production" && item.enabled === 1);
}
export function matchText(input: CaseMatchRequest): string {
  return [input.originalQuestion, input.firstAnswer, input.secondAnswer, input.thirdAnswer].join("\n");
}
/** 稳定散列：同一段旅程永远得到同一个编号，用来把不同用户分散到不同案例。 */
function journeyHash(input: CaseMatchRequest): number {
  let hash = 0;
  for (const character of matchText(input)) {
    hash = (hash * 31 + (character.codePointAt(0) ?? 0)) >>> 0;
  }
  return hash;
}
export function generatedJiaxingAnswer(input: CaseMatchRequest): string {
  const focus = input.originalQuestion.replace(/[？?。！!]/gu, "").slice(0, 20);
  const direction = input.topic === "service"
    ? "先找到一个真正需要帮助的人，从一次具体回应开始"
    : input.topic === "persistence"
      ? "先给坚持设一个短周期，在行动后记录变化，再决定下一步"
      : input.topic === "innovation"
        ? "先把想法缩小成一次低成本尝试，用真实反馈修正方向"
        : "先把问题缩小成明天能完成的一步，做完再根据结果调整";
  return `面对“${focus}”，可以先不等一个完整答案。${direction}。把结果留下来，让下一次选择比这一次更清楚。`;
}
// The model selects evidence, not new case facts. Both excerpts must exist verbatim.
export function resolveSelection(selection: CaseSelection, input: CaseMatchRequest, candidates: JiaxingCase[]): CaseMatchResult {
  if (selection.case_id === null) return { item: null, reason: "", generatedAnswer: selection.generated_answer.trim() || generatedJiaxingAnswer(input), method: "generated" };
  const item = eligibleCases(candidates).find(item => item.id === selection.case_id);
  const userExcerpt = selection.user_excerpt.trim();
  const caseExcerpt = selection.case_excerpt.trim();
  if (!item || userExcerpt.length < 2 || caseExcerpt.length < 2 ||
      !matchText(input).includes(userExcerpt) ||
      ![item.title, item.summary, item.content].some(text => text.includes(caseExcerpt))) {
    throw new Error("CASE_EVIDENCE_INVALID");
  }
  // 选中案例时也保留 AI 写的那段回答：页面上左栏是真实案例，右栏是这段回答。
  return { item, reason: `你刚才提到“${userExcerpt}”；这则案例中，“${caseExcerpt}”。`, generatedAnswer: selection.generated_answer.trim(), method: "ai" };
}
// Conservative local fallback: require a concrete shared term, never select by hash.
const TERMS = ["新方法", "尝试", "创新", "探索", "没有先例", "坚持", "长期", "调整", "学习", "服务", "帮助", "合作", "团队", "社区", "志愿", "环境", "技术", "乡村", "创业", "研究", "实验", "群众", "居民"];

/** 用户主题 → 案例主题：与内容层的映射保持一致，让主题兜底真的能命中。 */
const THEME_FOR_TOPIC: Record<string, string> = {
  innovation: "innovation",
  persistence: "persistence",
  responsibility: "responsibility",
  service: "responsibility",
  growth: "persistence",
  collaboration: "innovation",
  uncertainty: "innovation",
};

function caseBody(item: JiaxingCase): string {
  return [item.title, item.summary, item.content, ...(item.match_tags ?? [])].join("\n");
}

function tagText(item: JiaxingCase): string {
  return (item.match_tags ?? []).join("\n");
}

export function fallbackCaseMatch(input: CaseMatchRequest, candidates: JiaxingCase[]): CaseMatchResult {
  const text = matchText(input);
  const answer = generatedJiaxingAnswer(input);
  const ranked = eligibleCases(candidates).map(item => {
    const body = caseBody(item);
    const tags = tagText(item);
    const shared = TERMS.filter(term => text.includes(term) && body.includes(term));
    // 用户原问题里出现的词权重最高；命中人工标注的匹配标签再加一档
    const score = shared.reduce((sum, term) => sum + (input.originalQuestion.includes(term) ? 3 : 1) + (tags.includes(term) ? 2 : 0), 0);
    return { item, shared, score };
  }).filter(entry => entry.score >= 3).sort((a, b) => b.score - a.score || ((b.item.priority ?? 5) - (a.item.priority ?? 5)) || a.item.id.localeCompare(b.item.id));
  const best = ranked[0];
  if (best) return { item: best.item, reason: `你的表达与这则案例都提到了“${best.shared.join("、")}”。可以看看他们如何处理。`, generatedAnswer: answer, method: "keyword" };
  const published = eligibleCases(candidates);
  const wanted = input.topic ? THEME_FOR_TOPIC[input.topic] : undefined;
  const pool = wanted ? published.filter(item => item.theme === wanted) : published;
  // 主题兜底不再永远取第一条：按这次旅程的内容稳定地分散到不同案例
  const item = pool.length ? pool[journeyHash(input) % pool.length] : published[0];
  return item
    ? { item, reason: "这则已发布案例与你这次思考的主题相近，可以看看他们如何处理。", generatedAnswer: answer, method: "theme" }
    : { item: null, reason: "", generatedAnswer: answer, method: "generated" };
}
